import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock3, Instagram, MapPin, MessageCircle, Phone, QrCode } from "lucide-react";
import { Nav } from "./Nav";
import { CoverModal } from "./CoverModal";
import { FooterLinks } from "./SeoLanding";
import { MAPS_URL, PHONE, logClick, openWhatsApp, parsePrice, slugify, trackEvent } from "@/lib/site";
import { useMeta } from "@/lib/seo";
import { tiersOf } from "./EventsSection";

const EASE = [0.16, 1, 0.3, 1];

export const EventPage = ({ slug, events, eventsLoaded }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const event = eventsLoaded ? events.find((e) => slugify(e.title) === slug) : null;
  const tiers = event ? tiersOf(event) : [];
  const title = event ? `${event.title} at Soho Goa | ${event.date} ${event.month} – Nightclub in Baga` : "Event ended | Soho Goa – Nightclub in Baga";
  const description = event ? `${event.title} at Soho Goa, Baga — ${event.genre}. ${event.weekday} ${event.date} ${event.month}, ${event.time}. Couple, stag and female cover passes online; tables on WhatsApp.` : "This night at Soho Goa has ended — see what's coming up this week at Baga's Bollywood nightclub.";
  useMeta(title, description, `/events/${slug}`);

  useEffect(() => {
    if (!sessionStorage.getItem("soho_visit")) {
      sessionStorage.setItem("soho_visit", "1");
      logClick("visit");
    }
  }, []);

  useEffect(() => {
    if (!event) return undefined;
    const startDay = (String(event.date).match(/\d{1,2}/) || [])[0];
    const schema = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: `${event.title} at Soho Goa`,
      description: `${event.title} — ${event.genre} at Soho Goa nightclub, Baga. ${event.weekday}, ${event.time}.`,
      startDate: startDay ? `${event.date} ${event.month} ${event.time}` : undefined,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: { "@type": "Place", name: "Soho Goa - Nightclub", address: { "@type": "PostalAddress", streetAddress: "Calangute - Baga Rd, Baga", addressLocality: "Arpora", addressRegion: "Goa", postalCode: "403516", addressCountry: "IN" } },
      image: event.image,
      url: `https://sohogoa.com/events/${slug}`,
      offers: tiers.map((t) => ({ "@type": "Offer", name: `${t.label} entry`, price: parsePrice(t.price), priceCurrency: "INR", availability: "https://schema.org/InStock", url: `https://sohogoa.com/events/${slug}` })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "event-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById("event-schema")?.remove(); };
  }, [event, slug]);

  return (
    <div className="site-shell">
      <Nav />
      <main>
        {!eventsLoaded ? (
          <section className="event-hero event-ended"><p className="kicker">Soho Goa</p><h1>One <em>moment.</em></h1></section>
        ) : !event ? (
          <section className="event-hero event-ended" data-testid="event-ended">
            <p className="kicker">This night has passed</p>
            <h1>That party's <em>over.</em></h1>
            <p className="hero-copy">This event has ended or moved — but the floor never stays quiet for long. See what's on this week at Soho Goa.</p>
            <div className="hero-actions"><a href="/#events" className="button button-pink" data-testid="ended-events-link">See upcoming nights <ArrowRight size={17}/></a></div>
          </section>
        ) : (
          <section className="event-hero" data-testid="event-page">
            <motion.div className="event-hero-poster" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: EASE }}>
              <img src={event.image} alt={`${event.title} — Bollywood night at Soho Goa nightclub, Baga`}/>
              <div className="date-block"><strong>{event.date}</strong><span>{event.month}</span></div>
            </motion.div>
            <motion.div className="event-hero-info" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}>
              <p className="eyebrow"><span className="live-dot"/> {event.weekday} · {event.date} {event.month}</p>
              <h1>{event.title.split(" ").slice(0, -1).join(" ") || event.title} <em>{event.title.split(" ").slice(-1)}</em></h1>
              <p className="hero-copy">{event.genre} — live at Soho Goa, the Bollywood nightclub on Calangute–Baga Road, near Tito's Lane. Doors {event.time.toLowerCase().includes("pm") ? "open" : ""} {event.time}.</p>
              {tiers.length > 0 && (
                <div className="event-tiers" data-testid="event-tiers">
                  {tiers.map((t) => <div className="event-tier" key={t.key}><span>{t.label} entry</span><strong>{t.price}</strong></div>)}
                </div>
              )}
              <div className="hero-actions">
                <button className="button button-pink" onClick={() => { trackEvent("cover_charge_click", { event_name: event.title }); logClick("cover_click", event.title); setSelectedEvent(event); }} data-testid="event-page-buy"><QrCode size={17}/> Get cover charge</button>
                <button className="button button-ghost" onClick={() => openWhatsApp(`Hi Soho Goa, I'd like to reserve a table for ${event.title} on ${event.date} ${event.month}.`, event.title)} data-testid="event-page-reserve"><MessageCircle size={17}/> Book a table</button>
              </div>
              <div className="event-quickfacts">
                <span><Clock3 size={14}/>{event.time}</span>
                <a href={MAPS_URL} target="_blank" rel="noreferrer" onClick={() => { trackEvent("directions_click"); logClick("directions"); }} data-testid="event-page-directions"><MapPin size={14}/>Calangute–Baga Rd, Baga</a>
                <a href={`tel:${PHONE.replaceAll(" ", "")}`} onClick={() => { trackEvent("call_click", { phone: PHONE }); logClick("call"); }}><Phone size={14}/>{PHONE}</a>
              </div>
            </motion.div>
          </section>
        )}
        <section className="seo-cta">
          <p className="kicker">Soho Goa · Open 9 PM till late</p>
          <h2>See you <em>tonight.</em></h2>
          <div className="hero-actions" style={{ justifyContent: "center" }}>
            <a href="/#events" className="button button-pink">All this week's nights <ArrowRight size={17}/></a>
          </div>
        </section>
      </main>
      <footer>
        <div className="footer-top">
          <a className="brand" href="/"><img src="/logo.jpg" className="brand-logo" alt="Soho Goa nightclub Baga"/></a>
          <span>© 2026 Soho Goa. Stay out late.</span>
          <div><a href="https://instagram.com" target="_blank" rel="noreferrer"><Instagram size={18}/></a><span>All rights reserved.</span></div>
        </div>
        <FooterLinks />
        <div className="footer-mark" aria-hidden="true">SOHO</div>
      </footer>
      <CoverModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
};
